import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { colors } from '@/constants/colors';

const TIPS = [
   '💡 Tap the signal below — every boost warms up the server faster.',
   '📡 SmartFi vouchers: set duration, set price, print and sell.',
   '⚡ Free-tier servers hibernate after inactivity. Almost awake!',
   '🎯 Tip: the Admin Panel gives you real-time revenue charts.',
   '🚀 Fun fact: cold boots on Render average 30s.',
   '🎨 You can switch between 6 shop themes in Customization.',
   '📊 Pro plan removes the 20% platform fee on vouchers.',
];

const REVEAL_AFTER_MS = 1200; // keep the light HTML splash for quick reloads

const AppLoader = () => {
   const [visible, setVisible] = useState(false);
   const [taps, setTaps] = useState(0);
   const [elapsed, setElapsed] = useState(0);
   const [tipIdx, setTipIdx] = useState(0);
   const [bursts, setBursts] = useState<number[]>([]);
   const startRef = useRef<number>(Date.now());

   useEffect(() => {
      const revealTimer = setTimeout(() => setVisible(true), REVEAL_AFTER_MS);
      return () => clearTimeout(revealTimer);
   }, []);

   useEffect(() => {
      if (!visible) return;
      const tick = setInterval(() => {
         setElapsed(Math.floor((Date.now() - startRef.current) / 1000));
      }, 500);
      const tipRotate = setInterval(() => {
         setTipIdx((i) => (i + 1) % TIPS.length);
      }, 4500);
      return () => {
         clearInterval(tick);
         clearInterval(tipRotate);
      };
   }, [visible]);

   if (!visible) return null;

   // Fake progress — gentle creep, caps at 92 so it never looks "done"
   const progressFromTime = Math.min(92, (elapsed / 45) * 92);
   const progressFromTaps = Math.min(8, taps * 0.3);
   const progress = Math.min(99, progressFromTime + progressFromTaps);

   const handleTap = () => {
      setTaps((t) => t + 1);
      const id = Date.now() + Math.random();
      setBursts((b) => [...b, id]);
      window.setTimeout(() => {
         setBursts((b) => b.filter((x) => x !== id));
      }, 900);
   };

   return (
      <Overlay>
         <Bg />
         <Blob $top="10%" $left="15%" $delay="0s" />
         <Blob $top="70%" $left="80%" $delay="3s" />
         <Blob $top="40%" $left="60%" $delay="6s" />

         <Center>
            <LogoHeader>
               <LogoBadge>
                  <LogoImg src="/favicon.svg" alt="SmartFi" />
               </LogoBadge>
            </LogoHeader>

            <SignalArea onClick={handleTap}>
               <RingA />
               <RingB />
               <RingC />
               {bursts.map((id) => (
                  <Burst key={id} />
               ))}
               <TapBtn>
                  <WifiIcon viewBox="0 0 24 24" fill="none" stroke="currentColor">
                     <path d="M5 12.55a11 11 0 0 1 14 0" strokeWidth="2" strokeLinecap="round" />
                     <path d="M1.42 9a16 16 0 0 1 21.16 0" strokeWidth="2" strokeLinecap="round" />
                     <path d="M8.53 16.11a6 6 0 0 1 6.95 0" strokeWidth="2" strokeLinecap="round" />
                     <circle cx="12" cy="20" r="1.5" fill="currentColor" />
                  </WifiIcon>
                  <TapLabel>TAP TO BOOST</TapLabel>
               </TapBtn>
            </SignalArea>

            <Stats>
               <Stat>
                  <StatValue>{taps}</StatValue>
                  <StatLabel>Boosts</StatLabel>
               </Stat>
               <Divider />
               <Stat>
                  <StatValue>{elapsed}s</StatValue>
                  <StatLabel>Elapsed</StatLabel>
               </Stat>
               <Divider />
               <Stat>
                  <StatValue>{Math.round(progress)}%</StatValue>
                  <StatLabel>Signal</StatLabel>
               </Stat>
            </Stats>

            <ProgressBar>
               <ProgressFill style={{ width: `${progress}%` }}>
                  <ProgressShine />
               </ProgressFill>
            </ProgressBar>

            <Status>
               {elapsed < 5
                  ? 'Connecting…'
                  : elapsed < 15
                  ? 'Waking up the server…'
                  : elapsed < 30
                  ? 'Almost there — keep tapping!'
                  : 'Free tier server is yawning. Any second now ☕'}
            </Status>

            <Tip key={tipIdx}>{TIPS[tipIdx]}</Tip>
         </Center>
      </Overlay>
   );
};

const spin = keyframes`
   from { transform: rotate(0); }
   to { transform: rotate(360deg); }
`;
const expand = keyframes`
   0% { transform: scale(0.55); opacity: 0.9; }
   100% { transform: scale(1.6); opacity: 0; }
`;
const float = keyframes`
   0%, 100% { transform: translate(0, 0) scale(1); }
   50% { transform: translate(20px, -30px) scale(1.1); }
`;
const shine = keyframes`
   0% { left: -40%; }
   100% { left: 140%; }
`;
const pop = keyframes`
   0% { transform: scale(0.2); opacity: 1; }
   100% { transform: scale(2.2); opacity: 0; }
`;
const fadeIn = keyframes`
   from { opacity: 0; transform: translateY(6px); }
   to { opacity: 1; transform: translateY(0); }
`;
const bgShift = keyframes`
   0%, 100% { background-position: 0% 50%; }
   50% { background-position: 100% 50%; }
`;
const pulse = keyframes`
   0%, 100% { transform: translate(-50%, -50%) scale(1); box-shadow: 0 0 0 0 rgba(210, 253, 88, 0.45); }
   50% { transform: translate(-50%, -50%) scale(1.04); box-shadow: 0 0 0 20px rgba(210, 253, 88, 0); }
`;

const Overlay = styled.div`
   position: fixed;
   inset: 0;
   z-index: 9999;
   overflow: hidden;
   display: flex;
   align-items: center;
   justify-content: center;
   padding: 20px;
   font-family: Inter, system-ui, sans-serif;
`;

const Bg = styled.div`
   position: absolute;
   inset: 0;
   background: linear-gradient(
      135deg,
      #0b1030 0%,
      #1a1f4a 35%,
      #2a2165 70%,
      #0f1535 100%
   );
   background-size: 200% 200%;
   animation: ${bgShift} 12s ease infinite;
`;

const Blob = styled.div<{ $top: string; $left: string; $delay: string }>`
   position: absolute;
   top: ${({ $top }) => $top};
   left: ${({ $left }) => $left};
   width: 280px;
   height: 280px;
   border-radius: 50%;
   background: radial-gradient(
      circle,
      rgba(55, 96, 249, 0.35) 0%,
      rgba(55, 96, 249, 0) 70%
   );
   filter: blur(40px);
   animation: ${float} 10s ease-in-out infinite;
   animation-delay: ${({ $delay }) => $delay};
`;

const Center = styled.div`
   position: relative;
   z-index: 1;
   text-align: center;
   max-width: 400px;
   width: 100%;
   color: white;
`;

const LogoHeader = styled.div`
   margin-bottom: 10px;
`;

const LogoBadge = styled.div`
   width: 52px;
   height: 52px;
   margin: 0 auto;
   border-radius: 14px;
   background: white;
   display: grid;
   place-items: center;
   box-shadow: 0 8px 22px rgba(0, 0, 0, 0.35),
      0 0 0 1px rgba(255, 255, 255, 0.08) inset;
`;

const LogoImg = styled.img`
   width: 32px;
   height: 32px;
   object-fit: contain;
`;

const SignalArea = styled.div`
   position: relative;
   width: 200px;
   height: 200px;
   margin: 12px auto 18px;
   cursor: pointer;
   user-select: none;
   -webkit-tap-highlight-color: transparent;
`;

const Ring = styled.div`
   position: absolute;
   inset: 35px;
   border: 2px solid ${colors.SECONDARY};
   border-radius: 50%;
   opacity: 0;
`;
const RingA = styled(Ring)`
   animation: ${expand} 1.8s ease-out infinite;
`;
const RingB = styled(Ring)`
   animation: ${expand} 1.8s ease-out infinite;
   animation-delay: 0.6s;
`;
const RingC = styled(Ring)`
   animation: ${expand} 1.8s ease-out infinite;
   animation-delay: 1.2s;
`;

const Burst = styled.div`
   position: absolute;
   inset: 55px;
   border: 3px solid ${colors.SECONDARY};
   border-radius: 50%;
   animation: ${pop} 0.9s ease-out forwards;
   pointer-events: none;
`;

const TapBtn = styled.button`
   position: absolute;
   top: 50%;
   left: 50%;
   transform: translate(-50%, -50%);
   width: 120px;
   height: 120px;
   border-radius: 50%;
   background: linear-gradient(135deg, #3760f9 0%, #6a4dff 100%);
   color: white;
   border: none;
   cursor: pointer;
   display: flex;
   flex-direction: column;
   align-items: center;
   justify-content: center;
   gap: 4px;
   animation: ${pulse} 2s ease-in-out infinite;
   transition: transform 0.08s ease;

   &:active {
      animation: none;
      transform: translate(-50%, -50%) scale(0.92);
   }
`;

const WifiIcon = styled.svg`
   width: 34px;
   height: 34px;
`;

const TapLabel = styled.span`
   font-size: 10px;
   font-weight: 700;
   letter-spacing: 0.12em;
   opacity: 0.9;
`;

const Stats = styled.div`
   display: flex;
   align-items: center;
   justify-content: center;
   gap: 14px;
   margin-bottom: 12px;
`;

const Stat = styled.div`
   display: flex;
   flex-direction: column;
   align-items: center;
`;

const StatValue = styled.span`
   font-size: 18px;
   font-weight: 800;
   color: ${colors.SECONDARY};
   line-height: 1.1;
`;

const StatLabel = styled.span`
   font-size: 10px;
   text-transform: uppercase;
   letter-spacing: 0.1em;
   color: rgba(255, 255, 255, 0.55);
   margin-top: 2px;
`;

const Divider = styled.div`
   width: 1px;
   height: 22px;
   background: rgba(255, 255, 255, 0.15);
`;

const ProgressBar = styled.div`
   position: relative;
   height: 8px;
   border-radius: 999px;
   background: rgba(255, 255, 255, 0.08);
   overflow: hidden;
   margin-bottom: 14px;
`;

const ProgressFill = styled.div`
   position: relative;
   height: 100%;
   background: linear-gradient(90deg, ${colors.SECONDARY}, #7de8ff);
   border-radius: 999px;
   transition: width 0.3s ease;
   overflow: hidden;
`;

const ProgressShine = styled.div`
   position: absolute;
   top: 0;
   bottom: 0;
   width: 40%;
   background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.45) 50%,
      rgba(255, 255, 255, 0) 100%
   );
   animation: ${shine} 1.5s linear infinite;
`;

const Status = styled.p`
   font-size: 13px;
   color: rgba(255, 255, 255, 0.85);
   font-weight: 500;
   margin-bottom: 10px;
`;

const Tip = styled.p`
   font-size: 12px;
   color: rgba(255, 255, 255, 0.55);
   line-height: 1.5;
   min-height: 36px;
   animation: ${fadeIn} 0.4s ease;
   padding: 0 10px;
`;

export default AppLoader;
